import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiReports, { IOfferDashboard, IOfferDashboardOffer } from 'api/reports';
import DatePicker from 'components/layout/forms/DatePicker';
import PageContainer from 'components/layout/PageContainer';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';

import {
  BarList,
  Donut,
  Funnel,
  ISeries,
  StackedColumns,
} from './DashboardCharts';
import {
  customPeriod,
  daysOf,
  fiscalQuarter,
  fiscalYear,
  fiscalYearOf,
  IReportPeriod,
  lastMonths,
  lastWeek,
  monthsOf,
  thisWeek,
  today,
  yesterday,
} from './reportPeriods';

import 'style/reports/OfferDashboard.css';

// The colors of the statuses, the same in every chart and in the table
const STATUS_COLORS: { [status: string]: string } = {
  open: '#56ccf2',
  'ready-to-send': '#f2c94c',
  sent: '#2f80ed',
  approved: '#27ae60',
  rejected: '#eb5757',
  'not-realized': '#b8c0c8',
  lost: '#f2994a',
  'converted-preliminary-offer': '#9b51e0',
  'converted-offer': '#6c3fb5',
};

const STATUS_ORDER = Object.keys(STATUS_COLORS);

const TYPE_ORDER = [
  'opportunity',
  'preliminary-offer',
  'offer',
  'material-offer',
  'cost-estimate',
];

// The colors of the departments and work types in their bar charts
const PALETTE = [
  '#2f80ed',
  '#27ae60',
  '#9b51e0',
  '#f2994a',
  '#00a3a3',
  '#eb5757',
  '#f2c94c',
  '#56ccf2',
  '#6c3fb5',
  '#219653',
];

// The margin % marks of the calculation window: under 20 % red, over 30 % green
const getMarginColor = (ratio: null | number): string => {
  if (ratio === null) {
    return '#b8c0c8';
  }

  if (ratio < 20) {
    return '#eb5757';
  }

  return ratio > 30 ? '#27ae60' : '#e2b93b';
};

const getMarginRatio = (value: number, expense: number): null | number =>
  value > 0 ? ((value - expense) / value) * 100 : null;

// Euros shortly for the charts, e.g. "1,2 M€" or "350 t€"
const shortEuros = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return `${formatGroupedNumber(value / 1000000, 1, 1)} M€`;
  }

  if (Math.abs(value) >= 10000) {
    return `${formatGroupedNumber(value / 1000, 0, 0)} t€`;
  }

  return `${formatGroupedNumber(value, 0, 0)} €`;
};

const euros = (value: null | number | undefined): string =>
  value === null || value === undefined
    ? '–'
    : `${formatGroupedNumber(value, 0, 0)} €`;

// The sum of an offer in the dashboard: the estimate of an opportunity, else the offer
const getAmount = (offer: IOfferDashboardOffer): number =>
  (offer.type === 'opportunity' ? offer.estimate : offer.value) ?? 0;

type SortKey =
  | 'created'
  | 'customerName'
  | 'departmentName'
  | 'margin'
  | 'status'
  | 'targetName'
  | 'type'
  | 'amount';

// The periods of the choice: days and weeks, months back from now, and the fiscal year
const DAY_PERIODS = ['today', 'yesterday', 'this-week', 'last-week'];
const MONTH_PERIODS = [1, 2, 3, 6, 12, 24];
const QUARTERS = [1, 2, 3, 4];
// The fiscal years to choose from: the current one and the ones before it
const FISCAL_YEARS_BACK = 4;
// A period of at most this many days is shown by day, a longer one by month
const MAX_DAY_COLUMNS = 31;

const periodFor = (key: string, year: number, now: Date): IReportPeriod => {
  if (key === 'today') {
    return today(now);
  }
  if (key === 'yesterday') {
    return yesterday(now);
  }
  if (key === 'this-week') {
    return thisWeek(now);
  }
  if (key === 'last-week') {
    return lastWeek(now);
  }
  if (key.startsWith('months-')) {
    return lastMonths(now, parseInt(key.slice('months-'.length), 10));
  }
  if (key.startsWith('quarter-')) {
    return fiscalQuarter(year, parseInt(key.slice('quarter-'.length), 10));
  }
  return fiscalYear(year);
};

const OfferDashboard = ({
  tabs,
}: {
  // The tabs of the offer reports, above the title
  tabs?: JSX.Element;
}): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);
  const tr = (key: string, options?: any) =>
    t(`${translations.offers}:dashboard.${key}`, options);

  // The chosen period, e.g. 'months-12', 'this-week', 'fiscal-year' or 'quarter-2', and the
  // fiscal year of the fiscal periods (by the year it starts in)
  const [periodKey, setPeriodKey] = useState('months-12');
  const [year, setYear] = useState(() => fiscalYearOf(new Date()));
  // The days chosen from the calendar, both included
  const [customFrom, setCustomFrom] = useState<Date>(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [customTo, setCustomTo] = useState<Date>(() => new Date());
  const period = useMemo(
    () =>
      periodKey === 'custom'
        ? customPeriod(customFrom, customTo)
        : periodFor(periodKey, year, new Date()),
    [periodKey, year, customFrom, customTo]
  );
  const currentFiscalYear = fiscalYearOf(new Date());
  const [data, setData] = useState<IOfferDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [department, setDepartment] = useState<null | string>(null);
  const [type, setType] = useState<null | string>(null);
  const [status, setStatus] = useState<null | string>(null);
  const [measure, setMeasure] = useState<'amount' | 'count'>('amount');
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortAscending, setSortAscending] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    apiReports
      .getOfferDashboard(period.from, period.to)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [period]);

  // Every offer of the period is shown as it is
  const offers = data?.offers ?? [];

  const departments = useMemo(
    () =>
      Array.from(
        new Set(
          offers.map((offer) => offer.departmentName ?? tr('No department'))
        )
      ).sort(),
    [offers]
  );

  const departmentColors = useMemo(() => {
    const colors: { [name: string]: string } = {};
    departments.forEach((name, index) => {
      colors[name] = PALETTE[index % PALETTE.length];
    });
    return colors;
  }, [departments]);

  const departmentOf = (offer: IOfferDashboardOffer) =>
    offer.departmentName ?? tr('No department');

  // The offers chosen with the filters (each chart filters by the others)
  const filtered = useMemo(
    () =>
      offers.filter(
        (offer) =>
          (department === null || departmentOf(offer) === department) &&
          (type === null || offer.type === type) &&
          (status === null || offer.status === status)
      ),
    [offers, department, type, status]
  );

  const realOffers = filtered.filter((offer) => offer.type !== 'opportunity');
  const opportunities = filtered.filter(
    (offer) => offer.type === 'opportunity'
  );

  const offeredValue = realOffers.reduce(
    (total, offer) => total + (offer.value ?? 0),
    0
  );
  // The margin of all offers (opportunities have no calculation)
  const marginOffers = realOffers;
  const marginValue = marginOffers.reduce(
    (total, offer) => total + (offer.value ?? 0),
    0
  );
  const marginExpense = marginOffers.reduce(
    (total, offer) => total + (offer.expense ?? 0),
    0
  );
  const marginRatio = getMarginRatio(marginValue, marginExpense);

  // An approved preliminary offer was converted into an offer, it is not yet a deal
  const deals = realOffers.filter(
    (offer) => offer.type !== 'preliminary-offer'
  );
  const approved = deals.filter((offer) => offer.status === 'approved');
  const rejected = deals.filter((offer) => offer.status === 'rejected');
  const approvedValue = approved.reduce(
    (total, offer) => total + (offer.value ?? 0),
    0
  );
  const winRate =
    approved.length + rejected.length > 0
      ? (approved.length / (approved.length + rejected.length)) * 100
      : null;

  const openOffers = realOffers.filter(
    (offer) =>
      offer.status === 'open' ||
      offer.status === 'ready-to-send' ||
      offer.status === 'sent'
  );
  const openOpportunities = opportunities.filter(
    (offer) => offer.status === 'open'
  );
  const pipeline =
    openOffers.reduce((total, offer) => total + (offer.value ?? 0), 0) +
    openOpportunities.reduce(
      (total, offer) => total + (offer.estimate ?? 0),
      0
    );

  const measureOf = (offer: IOfferDashboardOffer) =>
    measure === 'amount' ? getAmount(offer) : 1;
  const formatMeasure = (value: number) =>
    measure === 'amount'
      ? shortEuros(value)
      : `${formatGroupedNumber(value, 0, 0)}`;

  // The days of a short period or the months of a longer one, by status
  const days = useMemo(() => daysOf(period), [period]);
  const byDay = days.length <= MAX_DAY_COLUMNS;
  const monthColumns = useMemo(() => {
    if (data === null) {
      return [];
    }

    const starts = byDay ? days : monthsOf(period);

    return starts.map((start) => {
      const values: { [status: string]: number } = {};

      filtered
        .filter((offer) => {
          const created = new Date(offer.created);
          return (
            created.getFullYear() === start.getFullYear() &&
            created.getMonth() === start.getMonth() &&
            (!byDay || created.getDate() === start.getDate())
          );
        })
        .forEach((offer) => {
          values[offer.status] = (values[offer.status] ?? 0) + measureOf(offer);
        });

      return {
        label: byDay
          ? `${start.getDate()}.${start.getMonth() + 1}.`
          : `${start.getMonth() + 1}/${`${start.getFullYear()}`.slice(2)}`,
        values,
      };
    });
  }, [data, filtered, period, days, byDay, measure]);

  const statusSeries: ISeries[] = STATUS_ORDER.filter((key) =>
    offers.some((offer) => offer.status === key)
  ).map((key) => ({
    color: STATUS_COLORS[key],
    key,
    label: t(`${translations.offers}:statuses.${key}`),
  }));

  // Status filter is not applied to the status donut itself, so all parts stay visible
  const forStatusDonut = offers.filter(
    (offer) =>
      (department === null || departmentOf(offer) === department) &&
      (type === null || offer.type === type)
  );
  const statusParts = statusSeries.map((item) => {
    const ofStatus = forStatusDonut.filter(
      (offer) => offer.status === item.key
    );
    const value = ofStatus.reduce(
      (total, offer) => total + measureOf(offer),
      0
    );
    return {
      color: item.color,
      key: item.key,
      label: item.label,
      text:
        measure === 'amount'
          ? shortEuros(value)
          : `${ofStatus.length} ${tr('pcs')}`,
      value,
    };
  });

  const departmentBars = departments
    .map((name) => {
      const ofDepartment = offers.filter(
        (offer) =>
          departmentOf(offer) === name &&
          (type === null || offer.type === type) &&
          (status === null || offer.status === status)
      );
      const value = ofDepartment.reduce(
        (total, offer) => total + measureOf(offer),
        0
      );
      const forMargin = ofDepartment.filter(
        (offer) => offer.type !== 'opportunity'
      );
      const offerValue = forMargin.reduce(
        (total, offer) => total + (offer.value ?? 0),
        0
      );
      const offerExpense = forMargin.reduce(
        (total, offer) => total + (offer.expense ?? 0),
        0
      );
      const ratio = getMarginRatio(offerValue, offerExpense);

      return {
        badge: {
          color: getMarginColor(ratio),
          text: ratio === null ? '–' : `${formatGroupedNumber(ratio, 0, 0)} %`,
        },
        color: departmentColors[name],
        key: name,
        label: name,
        text: formatMeasure(value),
        value,
      };
    })
    .filter((bar) => bar.value > 0)
    .sort((a, b) => b.value - a.value);

  // The work types of the positions of the chosen offers
  const filteredIds = new Set(realOffers.map((offer) => offer.id));
  const marginIds = new Set(marginOffers.map((offer) => offer.id));
  const workTypeTotals: {
    [group: string]: {
      expense: number;
      marginValue: number;
      value: number;
    };
  } = {};
  (data?.groups ?? [])
    .filter((group) => filteredIds.has(group.offerId))
    .forEach((group) => {
      const total = workTypeTotals[group.group] ?? {
        expense: 0,
        marginValue: 0,
        value: 0,
      };
      const forMargin = marginIds.has(group.offerId);
      workTypeTotals[group.group] = {
        expense: total.expense + (forMargin ? group.expense : 0),
        marginValue: total.marginValue + (forMargin ? group.value : 0),
        value: total.value + group.value,
      };
    });
  const workTypeBars = Object.keys(workTypeTotals)
    .map((group, index) => {
      const ratio = getMarginRatio(
        workTypeTotals[group].marginValue,
        workTypeTotals[group].expense
      );
      return {
        badge: {
          color: getMarginColor(ratio),
          text: ratio === null ? '–' : `${formatGroupedNumber(ratio, 0, 0)} %`,
        },
        color: PALETTE[(index + 3) % PALETTE.length],
        key: group,
        label: t(`${translations.products}:groups:${group}`),
        text: shortEuros(workTypeTotals[group].value),
        value: workTypeTotals[group].value,
      };
    })
    .sort((a, b) => b.value - a.value);

  // The steps of selling of the chosen offers
  const ofType = (offerType: string) =>
    filtered.filter((offer) => offer.type === offerType);
  const funnelSteps = [
    {
      label: tr('Opportunities'),
      list: ofType('opportunity'),
      color: '#56ccf2',
    },
    {
      label: tr('Preliminary offers'),
      list: ofType('preliminary-offer'),
      color: '#9b51e0',
    },
    { label: tr('Offers'), list: ofType('offer'), color: '#2f80ed' },
    {
      label: tr('Approved offers'),
      list: ofType('offer').filter((offer) => offer.status === 'approved'),
      color: '#27ae60',
    },
  ].map((step) => ({
    color: step.color,
    label: step.label,
    text: `${step.list.length} ${tr('pcs')} · ${shortEuros(
      step.list.reduce((total, offer) => total + getAmount(offer), 0)
    )}`,
    value: step.list.length,
  }));

  // The table of the chosen offers
  const sortValue = (offer: IOfferDashboardOffer): number | string => {
    switch (sortKey) {
      case 'amount':
        return getAmount(offer);
      case 'margin':
        return (
          getMarginRatio(offer.value ?? 0, offer.expense ?? 0) ?? -Infinity
        );
      case 'created':
        return offer.created;
      case 'departmentName':
        return departmentOf(offer);
      default:
        return `${offer[sortKey] ?? ''}`.toLowerCase();
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const first = sortValue(a);
    const second = sortValue(b);
    let order = 0;
    if (first < second) {
      order = -1;
    } else if (first > second) {
      order = 1;
    }
    return sortAscending ? order : -order;
  });
  const shown = showAll ? sorted : sorted.slice(0, 25);

  const sortArrow = sortAscending ? ' ▲' : ' ▼';

  // A win rate of 30 % or more is good
  let winRateColor = '#f2994a';
  if (winRate === null) {
    winRateColor = '#b8c0c8';
  } else if (winRate >= 30) {
    winRateColor = '#27ae60';
  }

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(
        key !== 'amount' && key !== 'created' && key !== 'margin'
      );
    }
  };

  const header = (key: SortKey, label: string, numeric = false) => (
    <th
      className={`${numeric ? 'numeric ' : ''}${
        sortKey === key ? 'sorted' : ''
      }`}
      onClick={() => sortBy(key)}
    >
      {label}
      {sortKey === key ? sortArrow : ''}
    </th>
  );

  const toggle = (current: null | string, value: string) =>
    current === value ? null : value;

  const kpis = [
    {
      color: '#2f80ed',
      label: tr('Offers'),
      note: tr('Opportunities count', { count: opportunities.length }),
      value: `${realOffers.length}`,
    },
    {
      color: '#6c3fb5',
      label: tr('Offered'),
      note: tr('VAT 0'),
      value: shortEuros(offeredValue),
    },
    {
      color: getMarginColor(marginRatio),
      label: tr('Margin'),
      note: euros(marginValue - marginExpense),
      value:
        marginRatio === null
          ? '–'
          : `${formatGroupedNumber(marginRatio, 1, 1)} %`,
    },
    {
      color: '#27ae60',
      label: tr('Approved'),
      note: tr('Approved count', { count: approved.length }),
      value: shortEuros(approvedValue),
    },
    {
      color: winRateColor,
      label: tr('Win rate'),
      note: tr('Win rate info'),
      value: winRate === null ? '–' : `${formatGroupedNumber(winRate, 0, 0)} %`,
    },
    {
      color: '#00a3a3',
      label: tr('Pipeline'),
      note: tr('Pipeline info'),
      value: shortEuros(pipeline),
    },
  ];

  const activeFilters = [
    department === null
      ? null
      : { label: department, clear: () => setDepartment(null) },
    type === null
      ? null
      : {
          label: t(`${translations.offers}:types.${type}`),
          clear: () => setType(null),
        },
    status === null
      ? null
      : {
          label: t(`${translations.offers}:statuses.${status}`),
          clear: () => setStatus(null),
        },
  ].filter((filter) => filter !== null) as {
    label: string;
    clear: () => void;
  }[];

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard">
        {tabs}
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters">
            <div className="offer-dashboard-periods">
              <div className="offer-dashboard-segments" role="group">
                {DAY_PERIODS.map((key) => (
                  <button
                    className={key === periodKey ? 'active' : ''}
                    key={key}
                    onClick={() => setPeriodKey(key)}
                    type="button"
                  >
                    {tr(`periods.${key}`)}
                  </button>
                ))}
              </div>
              <div className="offer-dashboard-segments" role="group">
                {MONTH_PERIODS.map((months) => (
                  <button
                    className={`months-${months}` === periodKey ? 'active' : ''}
                    key={months}
                    onClick={() => setPeriodKey(`months-${months}`)}
                    type="button"
                  >
                    {tr('Months', { count: months })}
                  </button>
                ))}
              </div>
              {/* The fiscal year 1.10.-30.9. and its quarters */}
              <div className="offer-dashboard-segments" role="group">
                <Input
                  aria-label={tr('periods.Fiscal year')}
                  bsSize="sm"
                  className="offer-dashboard-fiscal-year"
                  onChange={(event) => {
                    setYear(parseInt(event.target.value, 10));
                    if (
                      periodKey !== 'fiscal-year' &&
                      !periodKey.startsWith('quarter-')
                    ) {
                      setPeriodKey('fiscal-year');
                    }
                  }}
                  type="select"
                  value={year}
                >
                  {Array.from(
                    { length: FISCAL_YEARS_BACK + 1 },
                    (_, index) => currentFiscalYear - index
                  ).map((fiscal) => (
                    <option key={fiscal} value={fiscal}>
                      {tr('periods.Fiscal year label', {
                        from: fiscal,
                        to: `${fiscal + 1}`.slice(2),
                      })}
                    </option>
                  ))}
                </Input>
                <button
                  className={periodKey === 'fiscal-year' ? 'active' : ''}
                  onClick={() => setPeriodKey('fiscal-year')}
                  type="button"
                >
                  {tr('periods.Whole fiscal year')}
                </button>
                {QUARTERS.map((quarter) => (
                  <button
                    className={
                      `quarter-${quarter}` === periodKey ? 'active' : ''
                    }
                    key={quarter}
                    onClick={() => setPeriodKey(`quarter-${quarter}`)}
                    title={tr(`periods.Quarter months ${quarter}`)}
                    type="button"
                  >
                    {`Q${quarter}`}
                  </button>
                ))}
              </div>
              {/* Any days from the calendar */}
              <div
                className={`offer-dashboard-segments offer-dashboard-custom${
                  periodKey === 'custom' ? ' active' : ''
                }`}
                title={tr('periods.Custom info')}
              >
                <span className="offer-dashboard-custom-label">
                  {tr('periods.Custom')}
                </span>
                <DatePicker
                  endDate={customTo}
                  id="offer-dashboard-from"
                  onChange={(date) => {
                    if (date instanceof Date) {
                      setCustomFrom(date);
                      setPeriodKey('custom');
                    }
                  }}
                  selected={customFrom}
                  startDate={customFrom}
                />
                <span>–</span>
                <DatePicker
                  endDate={customTo}
                  id="offer-dashboard-to"
                  minDate={customFrom}
                  onChange={(date) => {
                    if (date instanceof Date) {
                      setCustomTo(date);
                      setPeriodKey('custom');
                    }
                  }}
                  popperPlacement="bottom-end"
                  selected={customTo}
                  startDate={customFrom}
                />
              </div>
            </div>
            <Input
              aria-label={tr('Department')}
              onChange={(event) =>
                setDepartment(
                  event.target.value === '' ? null : event.target.value
                )
              }
              type="select"
              value={department ?? ''}
            >
              <option value="">{tr('All departments')}</option>
              {departments.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Input>
            <Input
              aria-label={tr('Type')}
              onChange={(event) =>
                setType(event.target.value === '' ? null : event.target.value)
              }
              type="select"
              value={type ?? ''}
            >
              <option value="">{tr('All types')}</option>
              {TYPE_ORDER.map((offerType) => (
                <option key={offerType} value={offerType}>
                  {t(`${translations.offers}:types.${offerType}`)}
                </option>
              ))}
            </Input>
            <div className="offer-dashboard-segments" role="group">
              <button
                className={measure === 'amount' ? 'active' : ''}
                onClick={() => setMeasure('amount')}
                type="button"
              >
                €
              </button>
              <button
                className={measure === 'count' ? 'active' : ''}
                onClick={() => setMeasure('count')}
                type="button"
              >
                {tr('pcs')}
              </button>
            </div>
          </div>
        </div>

        {activeFilters.length > 0 ? (
          <div className="offer-dashboard-active-filters">
            {activeFilters.map((filter) => (
              <button key={filter.label} onClick={filter.clear} type="button">
                {filter.label} ✕
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <p className="offer-dashboard-info">{tr('Loading')}</p>
        ) : null}
        {failed ? (
          <p className="offer-dashboard-info">{tr('Load failed')}</p>
        ) : null}

        {data !== null && !loading ? (
          <>
            <div className="offer-dashboard-kpis">
              {kpis.map((kpi) => (
                <div
                  className="offer-dashboard-kpi"
                  key={kpi.label}
                  style={{ borderTopColor: kpi.color }}
                >
                  <div className="offer-dashboard-kpi-label">{kpi.label}</div>
                  <div
                    className="offer-dashboard-kpi-value"
                    style={{ color: kpi.color }}
                  >
                    {kpi.value}
                  </div>
                  <div className="offer-dashboard-kpi-note">{kpi.note}</div>
                </div>
              ))}
            </div>

            <div className="offer-dashboard-grid">
              <section className="offer-dashboard-card wide">
                <h2>
                  {tr(
                    `${byDay ? 'By day' : 'By month'} ${
                      measure === 'amount' ? 'amount' : 'count'
                    }`
                  )}
                </h2>
                <StackedColumns
                  columns={monthColumns}
                  format={formatMeasure}
                  series={statusSeries}
                />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By status')}</h2>
                <Donut
                  center={
                    measure === 'amount'
                      ? shortEuros(
                          statusParts.reduce(
                            (total, part) => total + part.value,
                            0
                          )
                        )
                      : `${forStatusDonut.length}`
                  }
                  centerLabel={
                    measure === 'amount' ? tr('Total') : tr('Offers')
                  }
                  onSelect={(key) => setStatus(toggle(status, key))}
                  parts={statusParts}
                  selected={status}
                />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By department')}</h2>
                <p className="offer-dashboard-card-info">
                  {tr('Margin badge info')}
                </p>
                <BarList
                  bars={departmentBars}
                  onSelect={(key) => setDepartment(toggle(department, key))}
                  selected={department}
                />
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('By work type')}</h2>
                <p className="offer-dashboard-card-info">
                  {tr('Work type info')}
                </p>
                {workTypeBars.length === 0 ? (
                  <p className="offer-dashboard-info">{tr('No data')}</p>
                ) : (
                  <BarList bars={workTypeBars} />
                )}
              </section>
              <section className="offer-dashboard-card">
                <h2>{tr('Funnel')}</h2>
                <p className="offer-dashboard-card-info">{tr('Funnel info')}</p>
                <Funnel steps={funnelSteps} />
              </section>
            </div>

            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>{tr('Offers of period', { count: filtered.length })}</h2>
              <div className="offer-dashboard-table-wrapper">
                <table className="offer-dashboard-table">
                  <thead>
                    <tr>
                      {header('targetName', tr('Target'))}
                      {header('customerName', tr('Customer'))}
                      {header('departmentName', tr('Department'))}
                      {header('type', tr('Type'))}
                      {header('status', tr('Status'))}
                      {header('created', tr('Created'), true)}
                      {header('amount', tr('Amount'), true)}
                      {header('margin', tr('Margin'), true)}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((offer) => {
                      const ratio =
                        offer.type === 'opportunity'
                          ? null
                          : getMarginRatio(
                              offer.value ?? 0,
                              offer.expense ?? 0
                            );

                      return (
                        <tr
                          key={offer.id}
                          onClick={() =>
                            history.push(`/offers/${offer.id}/${offer.type}`)
                          }
                        >
                          <td className="target">{offer.targetName}</td>
                          <td>{offer.customerName ?? ''}</td>
                          <td>
                            <span
                              className="dashboard-dot"
                              style={{
                                background:
                                  departmentColors[departmentOf(offer)],
                              }}
                            />
                            {departmentOf(offer)}
                          </td>
                          <td>
                            {t(`${translations.offers}:types.${offer.type}`)}
                          </td>
                          <td>
                            <span
                              className="offer-dashboard-status"
                              style={{
                                background:
                                  STATUS_COLORS[offer.status] ?? '#7f8c9a',
                              }}
                            >
                              {getOfferStatusLabel(t, offer.status, offer.type)}
                            </span>
                          </td>
                          <td className="numeric">
                            {new Date(offer.created).toLocaleDateString(
                              'fi-FI'
                            )}
                          </td>
                          <td className="numeric">
                            {offer.ndaRestricted
                              ? 'NDA'
                              : euros(getAmount(offer))}
                            {offer.type === 'opportunity' ? (
                              <span className="offer-dashboard-estimate">
                                {' '}
                                {tr('estimate')}
                              </span>
                            ) : null}
                          </td>
                          <td className="numeric">
                            {ratio === null ? (
                              '–'
                            ) : (
                              <span
                                className="offer-dashboard-margin"
                                style={{ borderColor: getMarginColor(ratio) }}
                              >
                                {`${formatGroupedNumber(ratio, 1, 1)} %`}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sorted.length > 25 ? (
                <button
                  className="offer-dashboard-show-all"
                  onClick={() => setShowAll(!showAll)}
                  type="button"
                >
                  {showAll
                    ? tr('Show less')
                    : tr('Show all', { count: sorted.length })}
                </button>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default OfferDashboard;
